import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from './ui/Card';
import usePermissions from '../hooks/usePermissions';
import { useAuth } from '../contexts/AuthContext';
import { organizationsService } from '../services/organizationsService';
import { membershipsOf } from '../services/permissions';

/**
 * Shown instead of a silent redirect when the selected organization grants this user nothing at
 * all, so they know why and can switch to an organization where they do have a role.
 */
function NoRoleNotice({ orgId, orgName }) {
  const { user } = useAuth();
  const others = membershipsOf(user).filter(
    (m) => String(m.organization_id) !== String(orgId) && (m.permissions || []).length > 0
  );
  const orgs = membershipsOf(user).map((m) => ({ id: String(m.organization_id), name: m.organization_name }));
  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <h2 className="text-lg font-semibold text-[#18181B]">No role in {orgName || 'this organization'}</h2>
        <p className="text-sm text-zinc-600">
          You have been added to {orgName || 'this organization'} but have not been assigned a role, so there is nothing to
          show here. Ask an administrator to assign you a role.
        </p>
        {others.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-zinc-500">Switch to:</span>
            {others.map((m) => (
              <button
                key={m.organization_id}
                type="button"
                onClick={() => organizationsService.setSelectedOrganization(String(m.organization_id), 'id', orgs)}
                className="rounded-md border border-hairline bg-surface px-2.5 py-1 text-sm font-medium text-[#18181B] hover:bg-[#E8EEF5]"
              >
                {m.organization_name}
              </button>
            ))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

/**
 * Route guard: renders children only if the user may open this page in the selected organization.
 * Otherwise sends them to the first page they can see. Re-checks when the org pulldown changes,
 * so switching to an org with a smaller role moves the user off a page they can no longer use.
 */
function RequirePermission({ children }) {
  const location = useLocation();
  const { canOpen, fallbackPath, orgId, orgName, permissions, isPlatformAdmin } = usePermissions();

  if (!canOpen(location.pathname)) {
    // An org with no role at all would otherwise bounce to /profile with no explanation.
    if (!isPlatformAdmin && orgId && permissions.size === 0 && location.pathname !== '/profile') {
      return <NoRoleNotice orgId={orgId} orgName={orgName} />;
    }
    const target = fallbackPath === location.pathname ? '/profile' : fallbackPath;
    return <Navigate to={target} replace />;
  }
  return children;
}

export default RequirePermission;
